import React, {FC, useEffect, useMemo, useRef, useState} from 'react';
import styles from './DefineFiscalYearStep.module.scss';
import {
    Button,
    Flex,
    Icon,
    PriorityIcon,
    RadioButton,
    TableColumn,
    Text
} from '@priority-software/priority-style-react';
import texts from '../../../texts';
import {
    getFiscalPeriodsForm,
    getFiscalYearForm,
    runOpenFiscalPeriodProc
} from '../../../actions/defineFiscalYear';
import {APIActionStatus, Column, Form, formsStore, Record} from '../../../../../stores/Form';
import _ from 'lodash';
import {useFooterContext} from '../../../hooks';
import {InputWithInnerState, Loader, Table} from '../../../Components';
import classnames from 'classnames';
import {observer, useObserver} from 'mobx-react';
import {Dictionary, NextState} from '../../../types';
import {useFiscalYear} from '../../../hooks/useFiscalYear';
import {undoAction, undoAll} from '../../../../Form/actions/undo';
import {RecordEditState} from '../../../../../stores/Form/enums';

const inputNames = ['GLNAME', 'SDATE', 'EDATE', 'PREFIX'];
const periodColumnNames = ['PERIOD', 'SDATE', 'EDATE', 'DAYS'];

const columnWidthMapping: Dictionary<number> = {
    GLNAME: 100,
    SDATE: 176,
    EDATE: 176,
    PREFIX: 120
};

const subFormPeriodColumn = 'PERIOD';
const subFormEndDateColumn = 'EDATE';

export const DefineFiscalYearStep: FC = observer(() => {
    const [loadingFiscalYearForm, setLoadingFiscalYearForm] = useState(false);
    const [loadingFiscalPeriodsForm, setLoadingFiscalPeriodsForm] = useState(false);
    const footerState = useFooterContext();
    const [radioValue, setRadioValue] = useState<number>();
    const [filterNew, setFilterNew] = useState<boolean>(true);
    const [fiscalYearStateLoaded, fiscalYearState] = useFiscalYear();
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const [fiscalYearForm, setFiscalYearForm] = useState<Form | null>(null);
    const fieldMapping = useRef(null);
    const endDateColumnIdRef = useRef<number>(null);

    useEffect(() => {
        setLoadingFiscalYearForm(true);
        if (fiscalYearStateLoaded) {
            getFiscalYearForm(fiscalYearState?.year.toString()).then(async form => {
                setFiscalYearForm(form);
                if (
                    fiscalYearState.existYearFlag ||
                    (form.records.length && !form.records[0].isNew)
                ) {
                    await getFiscalPeriodsForm(form);
                }
                setLoadingFiscalYearForm(false);
            });
            footerState.setIsValid(false);
        }
    }, [fiscalYearStateLoaded]);

    useEffect(() => {
        if (footerState.onNextState === NextState.Processing) {
            switch (radioValue) {
                case 0:
                case 1:
                    setLoadingFiscalPeriodsForm(true);
                    runOpenFiscalPeriodProc(
                        fiscalYearState?.year.toString(),
                        radioValue === 1
                            ? Number.parseInt(
                                  periodRecords[periodRecords.length - 1].getField(
                                      fiscalPeriodsForm.getColumnByName(subFormPeriodColumn)
                                          .id as number
                                  ).currentValue as string
                              )
                            : Number.parseInt(
                                  periodRecords[0].getField(
                                      fiscalPeriodsForm.getColumnByName(subFormPeriodColumn)
                                          .id as number
                                  ).currentValue as string
                              )
                    )
                        .then(() => {
                            formsStore.deleteForm(fiscalYearForm.session);
                            setLoadingFiscalPeriodsForm(false);
                            footerState.setOnNextState(NextState.Done);
                        })
                        .catch(() => {
                            setLoadingFiscalPeriodsForm(false);
                            footerState.setOnNextState(NextState.Idle);
                        });
                    break;
                default:
                    formsStore.deleteForm(fiscalYearForm.session);
                    footerState.setOnNextState(NextState.Done);
                    break;
            }
        }
    }, [footerState.onNextState]);

    const fiscalPeriodsForm: Form = useObserver(() => fiscalYearForm?.activeSubForm);

    const periodRecords = useObserver(
        () => fiscalPeriodsForm?.records.filter(record => (filterNew ? !record.isNew : true)) || []
    );

    useEffect(() => {
        if (radioValue !== undefined) {
            footerState.setIsValid(true);
        }
    }, [radioValue]);

    const onSubFormRowDeleteClick = async (row: Record) => {
        setLoadingFiscalPeriodsForm(true);
        if (row.isNew) {
            if (
                [RecordEditState.edit, RecordEditState.update].includes(
                    formsStore.getScreenBySession(fiscalYearForm.session).editMode
                )
            ) {
                await formsStore.getScreenBySession(fiscalYearForm.session).recordDeleteAction(row);
            }
            setFilterNew(true);
        } else {
            await formsStore.getScreenBySession(fiscalYearForm.session).recordDeleteAction(row);
        }
        setLoadingFiscalPeriodsForm(false);
    };

    const onSubFormInputBlur = (e: FocusEvent, row: Record, column: Column) => {
        if (row.getField(column.id).currentValue === row.getField(column.id).origValue) {
            return;
        }
        setLoadingFiscalPeriodsForm(true);
        fiscalPeriodsForm.setCurrentRow(row.recordIndex);
        fiscalPeriodsForm.setCurrentColumnId(column.id);
        formsStore
            .getScreenBySession(fiscalYearForm.session)
            .fieldUpdate(fiscalPeriodsForm.activeField())
            .then(async status => {
                if (status === APIActionStatus.passed) {
                    const period = fiscalPeriodsForm.currentRecord.getField(
                        fiscalPeriodsForm.getColumnByName(subFormPeriodColumn).id as number
                    ).currentValue as string;
                    const endDate = fiscalPeriodsForm.currentRecord.getField(
                        fiscalPeriodsForm.getColumnByName(subFormEndDateColumn).id as number
                    ).currentValue as string;
                    if (!!period && !!endDate) {
                        formsStore
                            .getScreenBySession(fiscalYearForm.session)
                            .saveRecord()
                            .then(async status => {
                                if (status === APIActionStatus.passed) {
                                    setFilterNew(true);
                                } else {
                                    await undoAction();
                                }
                                setLoadingFiscalPeriodsForm(false);
                            });
                    } else {
                        setLoadingFiscalPeriodsForm(false);
                    }
                } else {
                    fiscalPeriodsForm.currentRecord
                        .getField(column.id as number)
                        .setCurrentValue(
                            fiscalPeriodsForm.currentRecord.getField(column.id as number)
                                .origValue as string
                        );
                    await formsStore
                        .getScreenBySession(fiscalYearForm.session)
                        .fieldUpdate(fiscalPeriodsForm.activeField());
                    setLoadingFiscalPeriodsForm(false);
                }
            });
    };

    const tableColumns: TableColumn[] = useObserver(() =>
        fiscalPeriodsForm
            ? [
                  ...fiscalPeriodsForm.columns
                      .filter(column => periodColumnNames.includes(column.Name))
                      .map(
                          column =>
                              ({
                                  key: column.Title,
                                  title: column.Title,
                                  dataIndex: column.Title,
                                  width: columnWidthMapping[column.Name],
                                  render: (_, row: Record) => {
                                      return !['PERIOD', 'EDATE'].includes(column.Name) ? (
                                          <Text tag={'span'} skin={'paragraph-2'}>
                                              {row.getField(column.id).currentValue}
                                          </Text>
                                      ) : (
                                          <InputWithInnerState
                                              value={row.getField(column.id).currentValue as string}
                                              disabled={
                                                  loadingFiscalPeriodsForm ||
                                                  (!filterNew && !row.isNew)
                                              }
                                              onChange={event => {
                                                  row.getField(column.id).setCurrentValue(
                                                      event.target.value
                                                  );
                                              }}
                                              onBlur={(e: FocusEvent) =>
                                                  onSubFormInputBlur(e, row, column)
                                              }
                                          />
                                      );
                                  }
                              } as TableColumn)
                      ),
                  {
                      key: '',
                      title: '',
                      dataIndex: '',
                      width: 100,
                      render: (_, row: Record) => {
                          return (
                              <Flex justifyContent={'flex-start'} alignItems={'center'}>
                                  <Icon
                                      componentClasses={classnames(styles.deleteIcon, {
                                          [styles.activeIcon]:
                                              row.recordIndex ===
                                              (periodRecords.length > 0
                                                  ? periodRecords[periodRecords.length - 1]
                                                        ?.recordIndex
                                                  : undefined)
                                      })}
                                      icon={PriorityIcon.delete}
                                      iconColor={'secondary-200'}
                                      onClick={() => onSubFormRowDeleteClick(row)}
                                  />
                              </Flex>
                          );
                      }
                  }
              ]
            : []
    );

    const visibleColumnIds: number[] = useMemo(
        () =>
            fiscalYearForm
                ? Array.from(fiscalYearForm.columnsMap.values())
                      .filter(x => inputNames.includes(x.Name))
                      .map(x => x.ID as number)
                : [],
        [fiscalYearForm]
    );

    const inputs = useMemo(
        () =>
            fiscalYearForm
                ? _.sortBy(
                      fiscalYearForm.records[fiscalYearForm.currentRow].fields.filter(x =>
                          visibleColumnIds.includes(x.columnId)
                      ),
                      [o => visibleColumnIds.indexOf(o.columnId)]
                  )
                : undefined,
        [fiscalYearForm, visibleColumnIds, isInitialized]
    );

    useEffect(() => {
        if (!isInitialized && fiscalYearForm && fiscalYearStateLoaded) {
            const yearDateColumnId = fiscalYearForm.columns.find(x => x.Name === inputNames[0]).id;
            const startDateColumnId = fiscalYearForm.columns.find(x => x.Name === inputNames[1]).id;
            const endDateColumnId = fiscalYearForm.columns.find(x => x.Name === inputNames[2]).id;
            endDateColumnIdRef.current = endDateColumnId;
            const prefixColumnId = fiscalYearForm.columns.find(x => x.Name === inputNames[3]).id;

            const callback = async () => {
                fieldMapping.current = {
                    [yearDateColumnId]: fiscalYearState.year.toString(),
                    [startDateColumnId]: fiscalYearState.startDate,
                    [endDateColumnId]: fiscalYearState.endDate,
                    [prefixColumnId]: fiscalYearState.prefix
                };
                fiscalYearForm.setCurrentRow(0);

                setIsInitialized(true);
            };
            callback();
        }
    }, [fiscalYearForm, isInitialized, fiscalYearStateLoaded]);

    const createFiscalYearCallback = async () => {
        setLoadingFiscalPeriodsForm(true);
        await formsStore.getScreenBySession(fiscalYearForm.session).startRecordEdit(fiscalYearForm);
        for (const input of inputs) {
            fiscalYearForm.setCurrentColumnId(Number.parseInt(input.columnId.toString()));
            fiscalYearForm.activeField().setCurrentValue(fieldMapping.current[input.columnId]);
            input.currentValue = fieldMapping.current[input.columnId];
            const isOk: APIActionStatus = await formsStore
                .getScreenBySession(fiscalYearForm.session)
                .fieldUpdate();
            if (!isOk) {
                setLoadingFiscalPeriodsForm(false);
                await undoAll(formsStore.getScreenBySession(fiscalYearForm.session));
                return;
            }
        }
        const isOk = await formsStore.getScreenBySession(fiscalYearForm.session).saveRecord();
        if (isOk) {
            await getFiscalPeriodsForm(fiscalYearForm);
            setLoadingFiscalPeriodsForm(false);
        } else {
            await undoAll(formsStore.getScreenBySession(fiscalYearForm.session));
            setLoadingFiscalPeriodsForm(false);
        }
    };

    const onAddNewHandler = () => {
        setFilterNew(false);
    };

    return (
        <Flex
            componentClasses={classnames(styles.wrapper, {
                [styles.wrapperFullHeight]: !!fiscalPeriodsForm
            })}
            direction={'column'}
        >
            <Text tag={'span'} skin={'paragraph-1'} textColor={'primary-700'}>
                {texts['stage0_step1_explanation']}
            </Text>
            {inputs && isInitialized && !loadingFiscalYearForm && (
                <Flex direction={'row'} componentClasses={styles.inputs}>
                    {inputs.map((input, index) => {
                        const column = fiscalYearForm.getColumnByID(input.columnId);
                        return !fiscalPeriodsForm && !fiscalYearState?.existYearFlag ? (
                            <InputWithInnerState
                                key={index}
                                size={'large'}
                                disabled={!!fiscalPeriodsForm || !isInitialized}
                                readOnly={input.columnId !== endDateColumnIdRef.current}
                                value={fieldMapping.current[input.columnId]}
                                onChange={event => {
                                    fieldMapping.current[input.columnId] = event.target.value;
                                }}
                                label={
                                    <Text
                                        skin={'paragraph-1'}
                                        tag={'span'}
                                        textColor={'primary-700'}
                                    >
                                        {column.Title}
                                    </Text>
                                }
                                onFocus={() => {}}
                                required
                            />
                        ) : (
                            <Flex
                                direction={'row'}
                                justifyContent={'space-between'}
                                alignItems={'center'}
                            >
                                <Flex
                                    direction={'column'}
                                    alignItems={'flex-start'}
                                    justifyContent={'flex-end'}
                                >
                                    <Text
                                        tag={'span'}
                                        skin={'caption-2'}
                                        textColor={'secondary-500'}
                                    >
                                        {column.Title}
                                    </Text>
                                    <Text
                                        tag={'span'}
                                        skin={'paragraph-2'}
                                        textColor={'primary-700'}
                                    >
                                        {input.currentValue}
                                    </Text>
                                </Flex>
                            </Flex>
                        );
                    })}
                </Flex>
            )}
            {!fiscalPeriodsForm &&
                inputs &&
                !fiscalYearState?.existYearFlag &&
                !loadingFiscalYearForm && (
                    <div className={styles.buttonWrapper}>
                        <Button
                            onClick={() => createFiscalYearCallback()}
                            componentClasses={styles.createFiscalYearButton}
                            wideButton
                            disabled={loadingFiscalPeriodsForm}
                            size={'medium'}
                        >
                            {texts['create_fiscal_year']}
                        </Button>
                    </div>
                )}
            {fiscalPeriodsForm && <Table columns={tableColumns} data={periodRecords} />}
            {fiscalPeriodsForm && (
                <Flex direction={'row'} componentClasses={styles.subformOptions}>
                    <RadioButton
                        wrapperClasses={styles.radioButtons}
                        isHorizontal
                        selected={radioValue}
                        onChange={setRadioValue}
                        options={[
                            {
                                label: texts['open_first_period'],
                                value: 0
                            },
                            {
                                label: texts['open_all_periods'],
                                value: 1
                            },
                            {
                                label: texts['do_not_open_periods'],
                                value: 2
                            }
                        ]}
                    />
                    <Button
                        skin={'secondary'}
                        prefixIcon={PriorityIcon.newAdd}
                        onClick={onAddNewHandler}
                    >
                        {texts['add_new_period']}
                    </Button>
                </Flex>
            )}
            <Loader
                componentClassname={classnames(styles.loader, {
                    [styles.loaderForm]: loadingFiscalYearForm
                })}
                isVisible={loadingFiscalPeriodsForm || loadingFiscalYearForm}
            />
        </Flex>
    );
});
